import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Fab from '@mui/material/Fab'
import Modal from '@mui/material/Modal'
import classNames from 'classnames'
import _ from 'lodash'
import sortBy from 'lodash/sortBy'
import moment from 'moment'
import { VyneTable } from '@vynedental/design-system'

import Api from '../../Api'
import { setRedirect } from '../../appActions'
import { RootState } from '../../appReducer'
import { useAppDispatch } from '../../util/useAppDispatch'
import CopyText from '../shared/CopyText'
import { SearchBar } from '../shared/custom-fields/SearchBar'
import Paginator from '../shared/Paginator'

import { createAdmin, fetchAdminPermissions, fetchAdminsV2, updateAdminUnlockedStatus } from './actions'
import AdminItem from './AdminItem'
import ManageAdmin from './ManageAdmin'

import './AdminList.sass'

const AdminList = () => {
    const [showInviteModal, setShowInviteModal] = useState(false)

    const [searchTerms, setSearchTerms] = useState<ApiV2.Admin.AdminUsersSearchTerms>({
        page: 1,
        search: '',
        sort: 'created',
        order: 'desc',
    })

    const admins = useSelector((state: RootState) => state.admin.admins)
    const adminList = useSelector((state: RootState) => state.navigation.admins.list)
    const permissions = useSelector((state: RootState) => state.admin.permissions)
    const paginationInfo = useSelector((state: RootState) => state.admin.adminsPaginationInfo)

    const dispatch = useAppDispatch()
    const throttledFetchAdmins = useCallback(
        _.throttle((searchTerms: ApiV2.Admin.AdminUsersSearchTerms) => {
            dispatch(fetchAdminsV2('admins', { ...searchTerms }))
        }, 750),
        [],
    )

    useEffect(() => {
        dispatch(fetchAdminPermissions())
        async function fetchAdminData() {
            await Api.Admin.list('admins', { limit: 0 })
        }
        fetchAdminData()
    }, [dispatch])

    useEffect(() => {
        throttledFetchAdmins(searchTerms)
    }, [searchTerms, throttledFetchAdmins])

    const selectPage = (page: number) => {
        setSearchTermsState({ page })
    }
    const searchByKeyword = (e: React.ChangeEvent<HTMLInputElement>) => {
        const search = e.target && e.target.value
        setSearchTermsState({ search, page: 1 })
    }
    const clearSearch = () => {
        const search = ''
        setSearchTermsState({ search, page: 1 })
    }
    const setSearchTermsState = async (nextSearchTerms: ApiV2.Admin.AdminUsersSearchTerms) => {
        await setSearchTerms({
            ...searchTerms,
            ...nextSearchTerms,
        })
    }

    const handleTableChange = (pagination: any, filters: any, sorter: any) => {
        if (sorter && sorter.columnKey && sorter.order) {
            const order = sorter.order === 'ascend' ? 'asc' : 'desc'
            setSearchTermsState({ sort: sorter.columnKey, order })
        } else if (sorter && !sorter.order) {
            const flipped = searchTerms.order === 'desc' ? 'asc' : 'desc'
            setSearchTermsState({ order: flipped })
        }
    }

    const getSortOrder = (field: string) => {
        if (searchTerms.sort !== field) return null
        return searchTerms.order === 'asc' ? ('ascend' as const) : ('descend' as const)
    }

    const columns = [
        {
            title: 'Last Name, First Name',
            key: 'last_name',
            width: '20%',
            sorter: true,
            sortOrder: getSortOrder('last_name'),
            render: (_: any, admin: any) => `${admin.lastName} ${admin.firstName}`.trim(),
        },
        {
            title: 'Email Address',
            dataIndex: 'username',
            key: 'username',
            width: '20%',
            sorter: true,
            sortOrder: getSortOrder('username'),
            render: (username: string) => <CopyText text={username}>{username}</CopyText>,
        },
        {
            title: 'Acct. Type',
            key: 'account_type.value',
            width: '10%',
            sorter: true,
            sortOrder: getSortOrder('account_type.value'),
            render: (_: any, admin: any) => admin.type?.displayName?.replace('staff', 'Staff'),
        },
        {
            title: 'Permissions',
            key: 'permissions',
            width: '20%',
            render: (_val: any, admin: any) =>
                sortBy(admin.permissions, ['displayName']).map((p: any) => (
                    <div key={p.type}>{p.displayName}</div>
                )),
        },
        {
            title: 'Acct. Created',
            dataIndex: 'created',
            key: 'created',
            width: '10%',
            sorter: true,
            sortOrder: getSortOrder('created'),
            render: (created: string) => (created ? moment(created).format('MM/DD/YYYY') : ''),
        },
        {
            title: 'Acct. Status',
            key: 'status',
            width: '10%',
            render: (_: any, admin: any) => (admin.locked ? 'Locked' : admin.accountStatus?.display),
        },
        {
            title: '',
            key: 'manage',
            width: '10%',
            render: (_: any, admin: any) => <AdminItem admin={admin} />,
        },
    ]

    const dataSource = adminList.map(adminId => ({
        ...admins[adminId],
        key: adminId,
    }))

    const onShowInviteModal = () => {
        setShowInviteModal(true)
    }
    const onCloseInviteModal = () => {
        setShowInviteModal(false)
    }

    return (
        <div className={classNames('admin-list')}>
            <div className="bar">
                <div className="accounts-header">
                    <div className="accounts-statistics">
                        <div className="manage-shortcuts" onClick={() => dispatch(setRedirect('/admins/shortcuts'))}>
                            <i className="material-icons shortcuts-icon">speaker_notes</i>
                            Manage Shortcuts
                        </div>
                    </div>
                    <div className="invite-button">
                        <Fab
                            color="primary"
                            className={classNames('button')}
                            onClick={onShowInviteModal}
                            title="Invite new admin"
                        >
                            <i className="material-icons" style={{ color: 'black' }}>
                                add
                            </i>
                        </Fab>
                    </div>
                </div>
            </div>
            <div className="container">
                {showInviteModal && (
                    <Modal
                        className="pst-modal"
                        open={true}
                        onClose={(event, reason) => {
                            if (reason !== 'backdropClick') {
                                onCloseInviteModal()
                            }
                        }}
                        disableEscapeKeyDown={true}
                    >
                        <div className="contents manage-admin-modal">
                            <div className="close-modal-button" onClick={onCloseInviteModal}>
                                <i className="material-icons">close</i>
                            </div>
                            <ManageAdmin
                                permissionList={permissions}
                                submitButtonText="Create Admin"
                                onClose={onCloseInviteModal}
                                onSave={(admin: Api.AccountUpdate) => {
                                    dispatch(createAdmin(admin as Api.AccountCreate)).then(() => {
                                        throttledFetchAdmins(searchTerms)
                                        onCloseInviteModal()
                                    })
                                }}
                                updateStatusOnUnlockAccount={(adminId: string) =>
                                    dispatch(updateAdminUnlockedStatus(adminId))
                                }
                            />
                        </div>
                    </Modal>
                )}
                <SearchBar
                    value={searchTerms.search as string}
                    onChange={searchByKeyword}
                    onClear={clearSearch}
                    isMessageShown={(searchTerms.search as string).length > 0 && !adminList.length}
                    noResultsMessage="No records found for selected criteria"
                    placeholder="Search by Admin Name or Email Address"
                />
                <VyneTable
                    className="admin-table"
                    columns={columns}
                    dataSource={dataSource}
                    pagination={false}
                    tableLayout="fixed"
                    onChange={handleTableChange}
                    dataTestId="admin-table"
                />
                <div className="paginator-wrapper">
                    <Paginator
                        currentPage={searchTerms.page as number}
                        paginationInfo={paginationInfo}
                        selectPage={selectPage}
                    />
                </div>
            </div>
        </div>
    )
}

export default AdminList
